function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1]; 
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}















































/* =============================================
   ARTSCOUT.PRO — WCAG JS FIXES
   ============================================= */

(function () {
  var checkNav = setInterval(function () {
    var navs = document.querySelectorAll('.header__inline-menu');
    var isHomepage = window.location.pathname === '/' || (window.location.pathname.startsWith('/collections/') && !window.location.pathname.startsWith('/collections/artists') && !window.location.pathname.startsWith('/collections/all') && window.location.pathname !== '/collections' && !window.location.pathname.match(/^\/collections\/?$/) && !window.location.pathname.includes('/products/'));
    if (navs.length > 0) {
      if (isHomepage) {
        navs.forEach(function (nav) {
          nav.classList.add('nav-initial-state');
        });
      }
      clearInterval(checkNav);
    }
  }, 10);
})();

document.addEventListener('DOMContentLoaded', function () {

  /* ── 1. Alt text for decorative/functional images ── */
  const altFixes = [
    { src: 'arrow-right-black',   alt: 'Next Arrow' },
    { src: 'flip-arrow-left',     alt: 'Previous' },
    { src: 'banner-button-arrow', alt: '' },          // decorative
    { src: 'social-icon1',        alt: 'Facebook' },  // update to match actual icons
    { src: 'social-icon4',        alt: 'Instagram' },
    { src: 'social-icon2',        alt: 'X (Twitter)' },
    { src: 'social-icon3',        alt: 'Pinterest' },
  ];

  document.querySelectorAll('img').forEach(function (img) {
    if (img.hasAttribute('alt') && img.getAttribute('alt') !== '') return; // already has alt
    const src = img.getAttribute('src') || '';
    altFixes.forEach(function (fix) {
      if (src.includes(fix.src)) {
        img.setAttribute('alt', fix.alt);
      }
    });
    // Any remaining images with empty or missing alt that are inside <a> tags
    // (linked images) need at minimum empty alt="" — set role=presentation if decorative
    if (!img.hasAttribute('alt')) {
      img.setAttribute('alt', '');
    }
  });

  /* ── 2. Nav initial state: add class on load, remove on scroll ── */
var navItems = document.querySelectorAll('.header__inline-menu');
var isNavPage = window.location.pathname === '/' || (window.location.pathname.startsWith('/collections/') && !window.location.pathname.startsWith('/collections/artists') && !window.location.pathname.startsWith('/collections/all') && window.location.pathname !== '/collections' && !window.location.pathname.match(/^\/collections\/?$/) && !window.location.pathname.includes('/products/'));
if (isNavPage) {
  navItems.forEach(function(nav) { nav.classList.add('nav-initial-state'); });
}
var navEl = navItems[0];
if (navEl && isNavPage) {
  var headerWrapper = document.querySelector('.header-wrapper');
  var scrolled = false;
  function updateNavState() {
    var isScrolledDown = window.scrollY > 1;
    var hasColorScheme3 = headerWrapper && headerWrapper.classList.contains('color-scheme-3');
    if (!scrolled && isScrolledDown) {
      scrolled = true;
      navItems.forEach(function(n) { n.classList.remove('nav-initial-state'); });
    } else if (scrolled && !isScrolledDown && hasColorScheme3) {
      scrolled = false;
      navItems.forEach(function(n) { n.classList.add('nav-initial-state'); });
    }
  }
  window.addEventListener('scroll', updateNavState, { passive: true });
}

/* ── 3. Force dark grey on product card text ── */
  setTimeout(function () {
    document.querySelectorAll(
      '.product-card .collection-name h3, ' +
      '.product-card .artwork_name.collection__title, ' +
      '.product-card .price__regular .price-item.price-item--regular, ' +
      '.card-wrapper .artwork_name.collection__title, ' +
      '.card-wrapper .price__regular .price-item.price-item--regular, ' +
      '.card-wrapper .artwork-name-dimensions-wrapper h3'
    ).forEach(function (el) {
      el.style.setProperty('color', '#555555', 'important');
      el.style.setProperty('opacity', '1', 'important');
    });
  }, 500);

  /* ── 5. Fix slick slider list structure ── */
  function fixSlickStructure() {
    document.querySelectorAll('.art-hero__slider-nav').forEach(function (ul) {
      var divs = ul.querySelectorAll(':scope > div');
      divs.forEach(function (div) {
        var lis = div.querySelectorAll('li');
        lis.forEach(function (li) {
          ul.appendChild(li);
        });
        div.parentNode.removeChild(div);
      });
    });

    document.querySelectorAll('li.slick-slide').forEach(function (li) {
      if (!li.parentElement || (li.parentElement.tagName !== 'UL' && li.parentElement.tagName !== 'OL')) {
        var ul = document.createElement('ul');
        ul.style.listStyle = 'none';
        ul.style.margin = '0';
        ul.style.padding = '0';
        li.parentNode.insertBefore(ul, li);
        ul.appendChild(li);
      }
    });
  }

  fixSlickStructure();
  setTimeout(fixSlickStructure, 1000);

/* ── 6. Fix aria-hidden elements with focusable content ── */
  var isFixing = false;
  function fixAriaHiddenFocusable() {
    if (isFixing) return;
    isFixing = true;
    document.querySelectorAll('[aria-hidden="true"]').forEach(function (el) {
      if (el.closest('#carousel') || el.closest('.product-list') || el.closest('.collections-slider-section')) return;
      if (el.closest('.art-hero__slider-nav') || el.closest('.art-hero__menu')) return;
      if (el.hasAttribute('tabindex') && el.getAttribute('tabindex') !== '-1') {
        el.setAttribute('tabindex', '-1');
      }
      el.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach(function (focusable) {
        focusable.setAttribute('tabindex', '-1');
      });
    });
    isFixing = false;
  }

  /* ── Fix art hero nav tabs on mobile ── */
  function fixArtHeroNavMobile() {
    if (window.innerWidth > 767) return;
    if (document.querySelector('.art-hero__mobile-nav')) return;

    var navUl = document.querySelector('.art-hero__slider-nav');
    if (!navUl) return;

    var menu = document.querySelector('.art-hero__menu');
    if (!menu) return;

    var mobileNav = document.createElement('div');
    mobileNav.className = 'art-hero__mobile-nav';
    mobileNav.style.cssText = 'display:flex;overflow-x:auto;overflow-y:hidden;flex-wrap:nowrap;padding:8px 8px 8px 12px;-webkit-overflow-scrolling:touch;width:100%;box-sizing:border-box;background:#f3f3f3;border:1px solid #ddd;';

    var originalLis = navUl.querySelectorAll('li[data-tab]');
    if (originalLis.length === 0) {
      originalLis = document.querySelectorAll('.art-hero__slider-nav li');
    }

    originalLis.forEach(function(li) {
      var newLi = document.createElement('div');
      newLi.textContent = li.textContent.trim();
      newLi.setAttribute('data-tab', li.getAttribute('data-tab') || '');
      newLi.style.cssText = 'white-space:nowrap;flex-shrink:0;padding:10px 16px;font-size:13px;letter-spacing:1px;cursor:pointer;color:#333;';
      if (li.classList.contains('active') || li.classList.contains('slick-current')) {
        newLi.style.background = '#fff';
        newLi.style.border = '1px solid #ccc';
        newLi.style.fontWeight = 'bold';
      }
      newLi.addEventListener('click', function() {
        li.click();
        mobileNav.querySelectorAll('div').forEach(function(d) {
          d.style.background = '';
          d.style.border = '';
          d.style.fontWeight = '';
        });
        newLi.style.background = '#fff';
        newLi.style.border = '1px solid #ccc';
        newLi.style.fontWeight = 'bold';
      });
      mobileNav.appendChild(newLi);
    });

    navUl.style.cssText = 'display:none!important;visibility:hidden!important;height:0!important;overflow:hidden!important;';
    menu.appendChild(mobileNav);
  }

  setTimeout(fixArtHeroNavMobile, 2000);
  window.addEventListener('resize', fixArtHeroNavMobile);

  /* ── Fix magazine slider focus ── */
  function fixMagazineSliderFocus() {
    var pagesWrapper = document.querySelector('.magazine-flip-slider-pages-wrapper');
    if (pagesWrapper) {
      var allPages = pagesWrapper.querySelectorAll('.page');
      allPages.forEach(function (page) {
        page.querySelectorAll('a, button').forEach(function (el) {
          el.setAttribute('tabindex', '-1');
        });
      });
      var visibleContentPage = pagesWrapper.querySelector('.page-content:not(.flipped)');
      if (visibleContentPage) {
        visibleContentPage.querySelectorAll('a, button').forEach(function (el) {
          el.removeAttribute('tabindex');
        });
      }
    }

    var rightArrow = document.querySelector('.magazine-flip-slider-arrows.next');
    var leftArrow = document.querySelector('.magazine-flip-slider-arrows.left');
    if (rightArrow && rightArrow.style.display !== 'none') {
      rightArrow.setAttribute('tabindex', '0');
      rightArrow.setAttribute('role', 'button');
      rightArrow.setAttribute('aria-label', 'Next artist');
      rightArrow.addEventListener('keydown', function(e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          rightArrow.click();
          setTimeout(fixMagazineSliderFocus, 400);
        }
      });
    }
    if (leftArrow && leftArrow.style.display !== 'none') {
      leftArrow.setAttribute('tabindex', '0');
      leftArrow.setAttribute('role', 'button');
      leftArrow.setAttribute('aria-label', 'Previous artist');
      leftArrow.addEventListener('keydown', function(e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          leftArrow.click();
          setTimeout(fixMagazineSliderFocus, 400);
        }
      });
    }

    document.querySelectorAll('.mobile-magazine-slider .mobile-magazine-slider-item').forEach(function (item) {
      if (item.getAttribute('aria-hidden') === 'true') {
        item.querySelectorAll('a, button').forEach(function (el) {
          el.setAttribute('tabindex', '-1');
        });
      }
    });
  }

  fixMagazineSliderFocus();
  setTimeout(fixMagazineSliderFocus, 1000);

  fixAriaHiddenFocusable();

  var ariaObserver = new MutationObserver(function () {
    clearTimeout(ariaObserver._debounce);
    ariaObserver._debounce = setTimeout(function () {
      fixAriaHiddenFocusable();
      document.querySelectorAll('.art-hero__slider-nav li').forEach(function (el) {
        el.setAttribute('tabindex', '0');
        el.setAttribute('aria-hidden', 'false');
      });
    }, 200);
  });

  ariaObserver.observe(document.body, {
    attributes: true,
    subtree: true,
    attributeFilter: ['aria-hidden']
  });

  /* ── 7b. Fix paginate-numbers structure ── */
  document.querySelectorAll('.paginate-numbers').forEach(function (el) {
    if (el.tagName === 'DIV') {
      var lis = el.querySelectorAll('li');
      var parent = el.parentNode;
      lis.forEach(function (li) {
        parent.insertBefore(li, el);
      });
      parent.removeChild(el);
    }
  });

  /* ── 7c. Fix broken aria-labelledby references ── */
  document.querySelectorAll('[aria-labelledby]').forEach(function (el) {
    var ids = el.getAttribute('aria-labelledby').split(' ');
    var allExist = ids.every(function (id) {
      return document.getElementById(id);
    });
    if (!allExist) {
      var label = el.textContent.trim();
      if (label) {
        el.setAttribute('aria-label', label);
      }
      el.removeAttribute('aria-labelledby');
    }
  });


  /* ── 7d. Fix empty overlay links ── */
  document.querySelectorAll('a.overlay__link, a.full-unstyled-link').forEach(function (el) {
    if (!el.textContent.trim() && !el.querySelector('img') && !el.getAttribute('aria-label')) {
      var href = el.getAttribute('href') || '';
      var label = href.split('/').pop().replace(/-/g, ' ');
      if (label) {
        el.setAttribute('aria-label', label);
      } else {
        el.setAttribute('aria-hidden', 'true');
        el.setAttribute('tabindex', '-1');
      }
    }
  });

  /* ── 7. Make artwork-tabs items keyboard accessible ── */
  document.querySelectorAll('.artwork-tabs li').forEach(function (el) {
    if (!el.hasAttribute('tabindex')) {
      el.setAttribute('tabindex', '0');
    }
    el.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        el.click();
        setTimeout(function () {
          var carousel = document.getElementById('carousel');
          if (carousel) {
            var activeFilter = el.getAttribute('data-filter');
            var firstVisibleCard = carousel.querySelector('.product-card[data-tab="' + activeFilter + '"]');
            if (firstVisibleCard) {
              firstVisibleCard.setAttribute('tabindex', '0');
              firstVisibleCard.focus();
            }
          }
          var artGalleryWrapper = el.closest('.art-hero__slider-nav') || el.closest('[class*="slider"]') || el.closest('[class*="tab"]');
          if (!carousel) {
            el.click();
          }
        }, 150);
      }
    });
  });

  /* ── Make art gallery slider tabs keyboard accessible ── */
  var artworkTabItems = document.querySelectorAll('.artwork-tabs li');
  artworkTabItems.forEach(function (el, index) {
    el.setAttribute('role', 'tab');
    el.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        var next = artworkTabItems[index + 1] || artworkTabItems[0];
        next.focus();
        next.click();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        var prev = artworkTabItems[index - 1] || artworkTabItems[artworkTabItems.length - 1];
        prev.focus();
        prev.click();
      }
    });
  });

  setTimeout(function () {
    var heroNavItems = Array.from(document.querySelectorAll('.art-hero__slider-nav li'));
    heroNavItems.forEach(function (el, index) {
      el.setAttribute('tabindex', '0');
      el.setAttribute('aria-hidden', 'false');
      el.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          el.click();
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          var next = heroNavItems[index + 1] || heroNavItems[0];
          next.focus();
          next.click();
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          var prev = heroNavItems[index - 1] || heroNavItems[heroNavItems.length - 1];
          prev.focus();
          prev.click();
        }
      });
    });
  }, 1500);

  /* ── Make carousel image links focusable and skip text nodes ── */
  document.querySelectorAll('#carousel .product-card').forEach(function (card) {
    card.removeAttribute('tabindex');
    var link = card.querySelector('.artwork-image a');
    if (link) {
      link.setAttribute('tabindex', '0');
    }
    var infoLinks = card.querySelectorAll('.product-info a, .product-info [tabindex]');
    infoLinks.forEach(function (el) {
      el.setAttribute('tabindex', '-1');
    });
  });

 /* ── 8. Fix collection card link contrast ── */
  setTimeout(function () {
    document.querySelectorAll('.card-collection .full-unstyled-link, .card-collection2 .full-unstyled-link, .collection-card-wrapper .full-unstyled-link').forEach(function (el) {
      el.style.setProperty('color', '#000000', 'important');
      el.style.setProperty('opacity', '1', 'important');
    });
  }, 300);

  /* ── 8b. Fix collection page card contrast (axe) ── */
  setTimeout(function () {
    document.querySelectorAll(
      '.card__heading .full-unstyled-link, ' +
      '.card__heading a.full-unstyled-link, ' +
      '.card-collection .card__information .card__heading .full-unstyled-link, ' +
      '.card-collection .card__information .card__heading a, ' +
      '.card-collection2 .card__information .card__heading .full-unstyled-link, ' +
      '.card-collection2 .card__information .card__heading a'
    ).forEach(function (el) {
      el.style.setProperty('color', 'rgb(0, 0, 0)', 'important');
      el.style.setProperty('opacity', '1', 'important');
      el.style.setProperty('-webkit-text-fill-color', 'rgb(0, 0, 0)', 'important');
      el.style.removeProperty('background-color');
    });
    document.querySelectorAll(
      '.card__heading, ' +
      '.card-collection .card__information .card__heading, ' +
      '.card-collection2 .card__information .card__heading, ' +
      '.card__inner.color-scheme-2'
    ).forEach(function (el) {
      el.style.setProperty('color', 'rgb(0, 0, 0)', 'important');
      el.style.removeProperty('background-color');
    });
  }, 0);

  /* ── 6. Fix nested interactive controls in nav dropdowns ── */
  document.querySelectorAll('summary.header__menu-item a.header__menu-main-link').forEach(function (a) {
    a.setAttribute('tabindex', '-1');
    a.setAttribute('aria-hidden', 'true');
  });

  /* ── 4b. Fix broken JSON alt text on artist images ── */
  document.querySelectorAll('img').forEach(function (img) {
    var alt = img.getAttribute('alt') || '';
    var src = img.getAttribute('src') || '';
    var isJsonAlt = alt.indexOf('{') !== -1 && alt.indexOf('children') !== -1;
    var isArrow = src.indexOf('arrow') !== -1;
    var isSuspiciousArrowAlt = alt.trim().toLowerCase() === 'arrow' && isArrow;
    if (isJsonAlt) {
      var plainText = '';
      try {
        var extracted = alt.match(/"value"\s*:\s*"([^"]+)"/g);
        if (extracted && extracted.length > 0) {
          plainText = extracted.map(function(m) {
            return m.replace(/"value"\s*:\s*"/, '').replace(/"$/, '').trim();
          }).join(' ').replace(/\\n/g, ' ').trim();
        }
      } catch(e) {}
      if (!plainText) {
        plainText = src.split('/').pop().split('?')[0].replace(/[-_]/g, ' ').replace(/\.[^.]+$/, '');
      }
      img.setAttribute('alt', plainText);
    } else if (isSuspiciousArrowAlt) {
      img.setAttribute('alt', '');
      img.setAttribute('role', 'presentation');
    }
  });

 /* ── 4. Hide cart visually-hidden text from WAVE ── */
  document.querySelectorAll('.header__icon--cart .visually-hidden').forEach(function (el) {
    el.setAttribute('aria-hidden', 'true');
    el.style.setProperty('color', '#000000', 'important');
    el.style.setProperty('background-color', '#ffffff', 'important');
  });

  /* ── 4d. Fix nav span white on white on non-hero pages ── */
  var isHeroPage = window.location.pathname === '/' || (window.location.pathname.startsWith('/collections/') && !window.location.pathname.startsWith('/collections/artists') && !window.location.pathname.startsWith('/collections/all') && window.location.pathname !== '/collections' && !window.location.pathname.match(/^\/collections\/?$/) && !window.location.pathname.includes('/products/'));
  if (!isHeroPage) {
    document.querySelectorAll('.header__inline-menu .header__menu-item span').forEach(function (el) {
      el.style.setProperty('color', '#000000', 'important');
      el.style.setProperty('background-color', 'transparent', 'important');
    });
  }
  
  /* ── 4b. Hide price visually-hidden text from WAVE ── */
  document.querySelectorAll('.visually-hidden.visually-hidden--inline').forEach(function (el) {
    el.setAttribute('aria-hidden', 'true');
  });

  /* ── 4c. Fix broken alt image ── */
  document.querySelectorAll('img').forEach(function (img) {
    var src = img.getAttribute('src') || '';
    if (src.indexOf('original_2_12b87505-c647-4beb-b438-1103dc40b71e') !== -1) {
      img.setAttribute('alt', 'Muni Worm 4 by Miriam Sweeney');
      if (img.closest('a')) {
        img.closest('a').removeAttribute('aria-hidden');
        img.closest('a').setAttribute('tabindex', '0');
      }
    }
  });

  /* ── Truncate long alt text for WAVE ── */
  document.querySelectorAll('img').forEach(function (img) {
    var alt = img.getAttribute('alt') || '';
    if (alt.length > 100) {
      img.setAttribute('alt', alt.substring(0, 97) + '...');
    }
  });

  /* ── Fix missing form labels ── */
  var labelMap = [
    { name: 'contact[first_name]', label: 'Full Name' },
    { name: 'contact[email]',      label: 'Email Address' },
    { name: 'contact[phone]',      label: 'Phone Number' },
    { name: 'contact[body]',       label: 'Project Details' }
  ];
  labelMap.forEach(function (item) {
    var field = document.querySelector('input[name="' + item.name + '"], textarea[name="' + item.name + '"]');
    if (field && !field.getAttribute('aria-label') && !field.getAttribute('id')) {
      field.setAttribute('aria-label', item.label);
    }
  });



 });